import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} className="flex flex-col leading-none" data-testid="logo-link">
    <span className="font-serif text-2xl font-600 tracking-[0.28em] text-bz-navy">BENZENATI</span>
    <span className="mt-1 text-[0.6rem] uppercase tracking-[0.22em] text-bz-slate">
      Atelier de veille et de décryptage
    </span>
  </Link>
);

const navTestId = (to) => (to === "/" ? "home" : to.slice(1));

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-bz-line bg-bz-ivory/90 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-8 px-6 py-5 lg:px-10">
        <Logo />

        <nav className="hidden items-center gap-7 xl:gap-9 lg:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${navTestId(l.to)}`}
              className={({ isActive }) =>
                `bz-link-underline whitespace-nowrap text-sm font-400 transition-colors ${
                  isActive ? "text-bz-navy" : "text-bz-slate hover:text-bz-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden shrink-0 lg:block">
          <Link
            to="/newsletter"
            data-testid="header-cta-desktop"
            className="inline-flex items-center whitespace-nowrap border border-bz-navy px-5 py-2.5 text-xs uppercase tracking-[0.14em] text-bz-navy transition-colors duration-300 hover:bg-bz-navy hover:text-bz-ivory"
          >
            Recevoir les prochains décryptages
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="p-2 text-bz-navy lg:hidden"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} strokeWidth={1.4} /> : <Menu size={26} strokeWidth={1.4} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-bz-line bg-bz-ivory lg:hidden" data-testid="mobile-nav">
          <nav className="flex flex-col px-6 py-2">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${navTestId(l.to)}`}
                className={({ isActive }) =>
                  `border-b border-bz-line py-4 text-base font-400 ${isActive ? "text-bz-navy" : "text-bz-slate"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/newsletter"
              data-testid="header-cta-mobile"
              className="my-6 inline-flex items-center justify-center bg-bz-navy px-5 py-3.5 text-xs uppercase tracking-[0.15em] text-bz-ivory"
            >
              Recevoir les prochains décryptages
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
